import { Tooltip } from 'antd'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { API } from '@/api'
import MemberIcon from '@/assets/icons/member.svg?react'
import { Trans } from '@/components'
import { EmptyView } from '@/components/EmptyView.tsx'
import { MachineType } from '@/consts/machine'
import { SNAPSHOT_ALL_REGIONS_DOMAIN } from '@/consts/snapshot'
import { Machine } from '@/models/machine'
import { Snapshot } from '@/models/snapshot'
import {
  buildBaseResourceDesc,
  getBootSourceCompatibility,
  getMinimumInstanceCountForBootSource,
} from '@/pages/create-instance/utils/extraStorage'
import { formatDatePreset } from '@/utils/formatter'
import { fetchAllPages } from '@/utils/pagination'
import { cn } from '@/utils/style'

import { BOOT_SOURCE_PAGE_SIZE, BootSourcePagination } from './boot-source-pagination'
import { BootSourceSkeleton } from './boot-source-skeleton'
import { BootSourceCompatibilityMessage } from './BootSourceCompatibilityMessage'
import { MinimumResourceBadge } from './minimum-resource-badge'
import { SelectMachineWarning } from './select-machine-warning'

interface SnapshotSelectorProps<T extends Snapshot = Snapshot> {
  machine?: Machine
  value?: T
  type: MachineType
  count: number
  enforceResourceLimit?: boolean
  supportsExtraResources?: boolean
  onChange: (value?: T) => void
  onSnapshotsChange?: (value: T[]) => void
  additionalSnapshots?: T[]
  emptyMessageVariant?: 'default' | 'team'
  searchTerm?: string
}

export function SnapshotSelector<T extends Snapshot = Snapshot>({
  value,
  machine,
  type,
  count,
  enforceResourceLimit = false,
  supportsExtraResources = false,
  onChange,
  onSnapshotsChange,
  additionalSnapshots,
  emptyMessageVariant = 'default',
  searchTerm = '',
}: SnapshotSelectorProps<T>) {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [snapshots, setSnapshots] = useState<T[]>([])
  const [page, setPage] = useState(1)
  const requestSeqRef = useRef(0)
  const onSnapshotsChangeRef = useRef(onSnapshotsChange)
  const normalizedSearchTerm = searchTerm.trim().toLocaleLowerCase()

  useEffect(() => {
    onSnapshotsChangeRef.current = onSnapshotsChange
  }, [onSnapshotsChange])

  // Merge personal snapshots with additional snapshots (e.g., team snapshots)
  const allSnapshots = useMemo(() => {
    const snapshotsById = new Map<string, T>()
    for (const snapshot of [...(additionalSnapshots || []), ...snapshots]) {
      if (!snapshotsById.has(snapshot.resID)) snapshotsById.set(snapshot.resID, snapshot)
    }
    return [...snapshotsById.values()]
  }, [snapshots, additionalSnapshots])
  const filteredSnapshots = useMemo(() => {
    if (!normalizedSearchTerm) return allSnapshots

    return allSnapshots.filter((snapshot) =>
      [snapshot.id, snapshot.resID, snapshot.flagID, snapshot.name].some((value) =>
        String(value).toLocaleLowerCase().includes(normalizedSearchTerm),
      ),
    )
  }, [allSnapshots, normalizedSearchTerm])
  const visibleSnapshots = useMemo(() => {
    const start = (page - 1) * BOOT_SOURCE_PAGE_SIZE
    return filteredSnapshots.slice(start, start + BOOT_SOURCE_PAGE_SIZE)
  }, [filteredSnapshots, page])

  useEffect(() => {
    setPage(1)
  }, [machine?.key, type, count, additionalSnapshots?.length, normalizedSearchTerm])

  useEffect(() => {
    if (normalizedSearchTerm) return

    const selectedIndex = allSnapshots.findIndex((snapshot) => snapshot.resID === value?.resID)
    if (selectedIndex >= 0) {
      setPage(Math.floor(selectedIndex / BOOT_SOURCE_PAGE_SIZE) + 1)
    }
  }, [allSnapshots, normalizedSearchTerm, value?.resID])

  const getSnapList = useCallback(async () => {
    if (!machine) return
    const requestSeq = requestSeqRef.current + 1
    requestSeqRef.current = requestSeq
    setLoading(true)
    setSnapshots([])

    try {
      const serverSnapshots = await fetchAllPages({
        fetchPage: (page, pageSize) =>
          API.getSnapList({
            machine_category: type,
            page,
            per_page: pageSize,
            agent_id: machine.agent_id,
            owner_type: 1,
            status: [3],
            domains: [SNAPSHOT_ALL_REGIONS_DOMAIN],
            resource_desc: buildBaseResourceDesc(machine, count),
          }),
        getItems: (response) => response.snapshots,
      })

      if (requestSeq !== requestSeqRef.current) return

      const nextSnapshots = serverSnapshots.map((snap) => new Snapshot(snap)) as T[]

      setSnapshots(nextSnapshots)
      onSnapshotsChangeRef.current?.(nextSnapshots)
    } catch (error) {
      if (requestSeq !== requestSeqRef.current) return
      console.error('Failed to fetch complete snapshot list:', error)
      setSnapshots([])
    } finally {
      if (requestSeq === requestSeqRef.current) {
        setLoading(false)
      }
    }
  }, [machine, type, count])

  useEffect(() => {
    if (!machine) {
      requestSeqRef.current += 1
      setSnapshots([])
      return
    }

    getSnapList()
  }, [machine, count, getSnapList])

  if (!machine) {
    return <SelectMachineWarning />
  }

  return (
    <section
      className="bg-panel-white rounded-2xl py-6"
      aria-busy={loading}
      data-ai-component="snapshot-selector"
      data-ai-state={value ? 'selected' : 'needs-selection'}
      data-ai-value={value?.resID ?? ''}
    >
      {loading && <BootSourceSkeleton />}
      {!loading && filteredSnapshots.length > 0 && (
        <div
          className="grid h-[244px] grid-cols-4 grid-rows-2 gap-4 overflow-hidden"
          role="radiogroup"
          aria-label={t('create.bootSourceType.snapshot')}
        >
          {visibleSnapshots.map((snap) => {
            const snapAny = snap as any
            const isTeamSnapshot = snapAny.isTeamSnapshot
            const creatorName = snapAny.creatorName
            const minimumInstanceCount = getMinimumInstanceCountForBootSource({
              machine,
              count,
              source: snap,
              supportsExtraResources,
            })
            const resourceInsufficient = enforceResourceLimit && minimumInstanceCount === undefined
            const compatibility = getBootSourceCompatibility({
              machine,
              count: resourceInsufficient ? machine.maxAvailUnits : count,
              source: snap,
              supportsExtraResources,
            })
            const disabled = snap.disabled || resourceInsufficient
            const disabledReason = disabled ? (
              resourceInsufficient ? (
                <BootSourceCompatibilityMessage
                  compatibility={compatibility}
                  sourceType="snapshot"
                  supportsExtraResources={supportsExtraResources}
                />
              ) : (
                <div className="text-center t-sm">{snap.errMsg}</div>
              )
            ) : undefined
            const selected = value?.resID === snap.resID

            return (
              <Tooltip key={snap.resID} title={disabledReason}>
                <button
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  className={cn(
                    'h-full w-full overflow-hidden rounded-lg border border-neutral-200 p-4 text-left cursor-pointer',
                    {
                      'bg-highlight/10 border-secondary': selected,
                      'opacity-50 cursor-not-allowed': disabled,
                    },
                  )}
                  data-ai-component="snapshot-option"
                  data-ai-action="select-snapshot"
                  data-ai-state={disabled ? 'unavailable' : selected ? 'selected' : 'available'}
                  data-ai-value={snap.resID}
                  aria-disabled={disabled}
                  aria-label={`${snap.resID} ${snap.name}`}
                  onClick={(event) => {
                    event.stopPropagation()
                    if (!disabled) onChange(snap)
                  }}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <div className="min-w-0 flex-1 truncate t-xs text-primary">{snap.resID}</div>
                    <div className="flex shrink-0 items-center gap-1.5">
                      <MinimumResourceBadge resource={compatibility.required} />
                      {isTeamSnapshot ? (
                        <span className="badge-team px-1.5 text-primary-reverse inline-flex items-center gap-x-0.5">
                          <MemberIcon className="w-2.5 h-2.5" />
                          <span className="text-[10px] leading-[14px] font-bold">Team</span>
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <div className="t-sb mb-0.5 truncate text-primary">{snap.name}</div>
                  <p className="text-secondary t-xs">
                    Created at {formatDatePreset(snap.createdAt, 'minute')}
                  </p>
                  {creatorName ? (
                    <p className="text-secondary t-xs mt-1">By {creatorName}</p>
                  ) : null}
                </button>
              </Tooltip>
            )
          })}
        </div>
      )}
      {!loading && filteredSnapshots.length > 0 && (
        <BootSourcePagination
          current={page}
          selected={
            value && filteredSnapshots.some((snapshot) => snapshot.resID === value.resID) ? 1 : 0
          }
          total={filteredSnapshots.length}
          onChange={setPage}
        />
      )}
      {!loading && filteredSnapshots.length === 0 && (
        <EmptyView
          className="h-[300px]"
          title={
            <span className="t-sm text-secondary">
              {normalizedSearchTerm && allSnapshots.length > 0 ? (
                <Trans i18nKey="create.bootSourceSearchNoResults">No matching boot sources</Trans>
              ) : emptyMessageVariant === 'team' ? (
                <span className="block max-w-[760px] text-center leading-6">
                  <Trans i18nKey="customImage.filter.teamNoCompatibleImages">
                    No compatible images are currently available for this machine. Please contact
                    your administrator to configure the required compatible image.
                  </Trans>
                </span>
              ) : (
                <Trans i18nKey="common.emptyViewNoDataMsg">No Data</Trans>
              )}
            </span>
          }
        />
      )}
    </section>
  )
}
